import React from "react";
import { ReferenceArea, ReferenceLine } from "recharts";

import {
	addMilliseconds,
	differenceInMilliseconds,
	getTime,
	isAfter,
	isBefore,
	subMilliseconds,
} from "~/tools/date-time";
import { useScoped } from "~/tools/impulse";
import { sortedIndexWith } from "~/tools/sorted-index-with";

import { cx } from "../../../tools/class-variance";
import { useTimestampCursor, useTimestampCursorState } from "../TimestampCursor";

import type { ChartDataPoint } from "./_create-chart-data-points";
import styles from "./_global-cursor.module.css";

// Offset is used to align the ReferenceArea to the built-in recharts hover effect for bar charts
const REFERENCE_AREA_Y_OFFSET = 14;

interface AreaCursorProps {
	className?: string;
	cursorValue: number;
	points: ReadonlyArray<ChartDataPoint>;
}

const AreaCursor: React.FC<AreaCursorProps> = ({ points, cursorValue, className }) => {
	const pointInterval = points.length >= 2 ? differenceInMilliseconds(points[1].time, points[0].time) : undefined;
	const indexContainingCursor = React.useMemo(() => {
		if (pointInterval == null) {
			return undefined;
		}

		return sortedIndexWith(points, ({ time }) => isBefore(time, subMilliseconds(cursorValue, pointInterval / 2)));
	}, [pointInterval, points, cursorValue]);

	// If we don't have at least two points for the point interval,
	// Or the cursor does not fall between any of the points
	// We do not render the area
	if (pointInterval == null || indexContainingCursor == null) {
		return null;
	}

	const pointContainingCursor = points[indexContainingCursor];
	const latestPoint = points.at(points.length - 1);

	// If the cursorValue goes past the final bar, we don't render the area
	if (latestPoint == null || isAfter(cursorValue, addMilliseconds(latestPoint.time, pointInterval / 2))) {
		return null;
	}

	const areaWidth = pointInterval;
	const areaStartingX = pointContainingCursor.time;
	const areaEndingX = addMilliseconds(areaStartingX, areaWidth);

	return (
		<ReferenceArea
			ifOverflow="visible"
			shape={({ x, y, width, height }: { x: number; y: number; width: number; height: number }) => (
				<rect
					className={className}
					height={height + REFERENCE_AREA_Y_OFFSET}
					width={width}
					// Offsets the x so that it covers the bar
					x={x - width / 2}
					y={y - REFERENCE_AREA_Y_OFFSET}
				/>
			)}
			x1={getTime(areaStartingX)}
			x2={getTime(areaEndingX)}
		/>
	);
};

interface Props {
	className?: string;
	/**
	 * When set, will render an area calculated by the points interval along
	 * with the line. When unset, will render just a line.
	 */
	points?: Array<ChartDataPoint>;
}

export const GlobalCursor: React.FC<Props> = ({ points, className }: Props) => {
	const timestampCursor = useTimestampCursor();
	const timestampCursorState = useTimestampCursorState();

	const cursor = useScoped(
		(scope) => {
			const value = timestampCursor.getValue(scope);

			if (value == null) {
				return null;
			}

			const isHotkeyPressed = timestampCursorState.isHotkeyPressed(scope);

			return { value, isHotkeyPressed };
		},
		[timestampCursor, timestampCursorState],
	);

	if (cursor == null) {
		return null;
	}

	const renderArea = points != null;

	return (
		<>
			<ReferenceLine
				ifOverflow="visible"
				shape={(props) => (
					<line
						{...props}
						className={cx(
							styles.referenceLine,
							cursor.isHotkeyPressed && styles.highlightedStroke,
							!renderArea && className,
						)}
					/>
				)}
				x={cursor.value}
				zIndex={500}
			/>
			{renderArea && (
				<AreaCursor
					className={cx(styles.referenceArea, cursor.isHotkeyPressed && styles.highlightedStroke, className)}
					cursorValue={cursor.value}
					points={points}
				/>
			)}
		</>
	);
};
